import { LeadTimeBreakdown } from './LeadTimeBreakdown';
import { useEffect, useRef, useState } from 'react';
import { useGT } from 'gt-react';
import { ClockIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TooltipInfoIcon } from '@/components/common/TooltipInfoIcon';
import { LeadTimeGrainSchema } from '@erp/rpc';
import type { DeclaredLeadTime, LeadTimeOverrideRow } from '@erp/rpc';
import { useLeadTimeDetail } from '@/api/rpc/hooks/procurement/useLeadTimeDetail';
import { LeadTimeDetailOverrideBlock } from './LeadTimeDetailOverrideBlock';
import type {
  GetLeadTimeDetailResponse,
  LeadTimeRung,
} from '@/api/rpc/hooks/procurement/useLeadTimeDetail';

// "Expected to arrive in ~N days" + the click-through calculation detail:
// every observed-lead-time ladder rung for the (product, warehouse) with its
// median / sample count, which rung the resolver picked, and the flat
// fallback. When a manual override is active it sits above the ladder as the
// picked row, with the ladder's own answer kept beside it (GAINS shows the
// calculated value next to the override; so do we). Measured vendor lead
// times only — transfer transit is a fixed assumption with nothing to
// explain, so transfer rows render no annotation.
//
// The vendors' DECLARED days sit in their own block below the ladder, never
// in it: they are not measured, and policy math does not use them. They are
// shown because phase 2 falls back to them for a buy leg's arrival estimate,
// so the same series can read "no estimate" on the inventory page and "~6d"
// on a suggestion, which looks like a contradiction until each number is
// labelled with where it came from.

interface LeadTimeIdentity {
  /** ProductV3 key (the SKU): the product identity procurement resolves on. */
  productV3Key?: string | null;
  /** Null disables the rung query (callers without a warehouse context). */
  warehouseId: string | null;
}

// A component (not a helper taking gt as a parameter) so the gt CLI can
// extract the strings — it only traces gt() calls fed by a local useGT().
function RungScopeLabel({ grain }: { grain: LeadTimeRung['grain'] }) {
  const gt = useGT();
  switch (grain) {
    case 'item_loc':
      return gt('This item at this warehouse', { context: 'lead time rung' });
    case 'item':
      return gt('This item across the division', { context: 'lead time rung' });
    case 'item_national':
      return gt('This item org-wide (national)', { context: 'lead time rung' });
  }
}

function RungRow({ rung }: { rung: LeadTimeRung }) {
  const gt = useGT();
  return (
    <tr className={rung.picked ? 'bg-emerald-50' : undefined}>
      <td className="py-1 pr-3 text-xs text-gray-700">
        <RungScopeLabel grain={rung.grain} />
        {rung.picked && (
          <span className="ml-2 rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-medium text-emerald-700">
            {gt('picked', { context: 'lead time rung marker' })}
          </span>
        )}
      </td>
      <td className="py-1 pr-3 text-right text-xs tabular-nums text-gray-900">
        {gt('~{days}d', {
          context: 'lead time rung median',
          days: Math.round(rung.p50Days),
        })}
      </td>
      <td className="py-1 pr-3 text-right text-xs tabular-nums text-gray-500">
        {rung.sigmaDays !== null
          ? gt('±{sigma}d', {
              context: 'lead time rung spread',
              sigma: Math.round(rung.sigmaDays),
            })
          : '—'}
      </td>
      <td className="py-1 text-right text-xs tabular-nums">
        <span
          className={rung.meetsMinSamples ? 'text-gray-500' : 'text-amber-600'}
        >
          {gt('{n} receipts', {
            context: 'lead time rung sample count',
            n: rung.sampleN,
          })}
        </span>
      </td>
    </tr>
  );
}

// The active manual override, rendered above the ladder as the picked row:
// its pinned days, the window it lapses or comes up for review, who set it
// and why. Spread and sample stay empty: nothing was measured.
function OverrideRow({ override }: { override: LeadTimeOverrideRow }) {
  const gt = useGT();
  const window = override.endsAt
    ? gt('until {date}', {
        context: 'lead time override window',
        date: new Date(override.endsAt).toLocaleDateString(),
      })
    : gt('open-ended', { context: 'lead time override window' });
  const review = override.reviewAt
    ? gt('review by {date}', {
        context: 'lead time override review date',
        date: new Date(override.reviewAt).toLocaleDateString(),
      })
    : null;
  return (
    <tr className="bg-sky-50">
      <td className="py-1 pr-3 text-xs text-gray-700">
        {gt('Total lead time override', { context: 'lead time rung' })}
        <span className="ml-2 rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium text-sky-700">
          {gt('picked', { context: 'lead time rung marker' })}
        </span>
        <div className="text-[10px] text-gray-500">
          {[window, review, override.createdByUserName]
            .filter((part): part is string => !!part)
            .join(' · ')}
        </div>
        {override.reason ? (
          <div className="text-[10px] italic text-gray-500">
            {override.reason}
          </div>
        ) : null}
      </td>
      <td className="py-1 pr-3 text-right text-xs tabular-nums text-gray-900">
        {gt('{days}d', {
          context: 'lead time override days',
          days: override.leadTimeDays,
        })}
      </td>
      <td className="py-1 pr-3 text-right text-xs text-gray-400">—</td>
      <td className="py-1 text-right text-xs text-gray-400">—</td>
    </tr>
  );
}

// A ladder level with no stats row: zero valid receipts in the window. Shown
// anyway so the ladder always reads complete.
function EmptyRungRow({ grain }: { grain: LeadTimeRung['grain'] }) {
  const gt = useGT();
  return (
    <tr className="text-gray-300">
      <td className="py-1 pr-3 text-xs">
        <RungScopeLabel grain={grain} />
      </td>
      <td className="py-1 pr-3 text-right text-xs">—</td>
      <td className="py-1 pr-3 text-right text-xs">—</td>
      <td className="py-1 text-right text-xs tabular-nums">
        {gt('{n} receipts', {
          context: 'lead time rung sample count',
          n: 0,
        })}
      </td>
    </tr>
  );
}

/** Stable per declared entry, and the same identity the backend dedups on. */
function declaredKey(declared: DeclaredLeadTime): string {
  return `${declared.supplierId}|${declared.warehouseScoped}|${declared.leadTimeDays}`;
}

/**
 * The vendors' own declared days, kept out of the ladder table so nothing
 * implies they were measured. The footnote is the whole point of the block:
 * it names which engine uses this number and which one ignores it, and so
 * depends on whether the ladder answered at all.
 */
function DeclaredLeadTimePanel({
  declared,
  fallbackDays,
  hasResolvedValue,
}: {
  declared: readonly DeclaredLeadTime[];
  fallbackDays: number;
  hasResolvedValue: boolean;
}) {
  const gt = useGT();
  if (declared.length === 0) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs font-medium text-gray-700">
        {gt('Declared by the supplier', {
          context: 'declared lead time panel label',
        })}
      </p>
      <ul className="mt-1 space-y-0.5">
        {declared.map((entry) => (
          <li
            key={declaredKey(entry)}
            className="flex items-baseline justify-between gap-3 text-xs text-gray-700"
          >
            <span>
              {entry.supplierName}
              {!entry.warehouseScoped && (
                <span className="ml-1 text-[10px] text-gray-500">
                  {gt('(all warehouses)', {
                    context: 'declared lead time row scope',
                  })}
                </span>
              )}
            </span>
            <span className="font-medium tabular-nums text-gray-900">
              {gt('{days}d', {
                context: 'declared lead time days',
                days: Math.round(entry.leadTimeDays),
              })}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] text-gray-500">
        {hasResolvedValue
          ? gt(
              'Supplier-declared PO-to-receipt is separate from the observed measurement. Calculated sources use the observed base when available; current and saved totals above include applicable components.',
              { context: 'declared lead time footnote (measured)' },
            )
          : gt(
              'Without observed history, buy estimates can use declared PO-to-receipt as their base. Forecast policy uses its own assumed base (~{fallback} days). Eligible lead time components are added separately; current and saved totals are shown above.',
              {
                context: 'declared lead time footnote (unmeasured)',
                fallback: fallbackDays,
              },
            )}
      </p>
    </div>
  );
}

/**
 * One line saying which number the consumers actually use and why. Extracted
 * from the dialog body because the case split (override / measured /
 * unmeasured, each with its own wording) is the bulk of the dialog's logic.
 */
function ResolutionSummary({ detail }: { detail: GetLeadTimeDetailResponse }) {
  const gt = useGT();

  if (detail.override) {
    return detail.observed.leadTimeDays !== null
      ? gt(
          'Using {days} days from a total lead time override. Observed PO-to-receipt is ~{observed} days ({n} receipts).',
          {
            context: 'lead time resolution summary (override)',
            days: detail.override.leadTimeDays,
            observed: Math.round(detail.observed.leadTimeDays),
            n: detail.observed.sampleN,
          },
        )
      : gt(
          'Using {days} days from a total lead time override. No observed PO-to-receipt measurement clears the {min}-receipt minimum.',
          {
            context: 'lead time resolution summary (override, unmeasured)',
            days: detail.override.leadTimeDays,
            min: detail.minSamples,
          },
        );
  }

  if (detail.resolved.leadTimeDays === null) {
    // With a declared value present the split between the two engines needs
    // more than a sentence, so the declared block's footnote carries it and
    // this line states only what the ladder found.
    return detail.declared.length > 0
      ? gt(
          'No observed PO-to-receipt measurement clears the {min}-receipt minimum.',
          {
            context: 'lead time resolution summary (unmeasured, declared)',
            min: detail.minSamples,
          },
        )
      : gt(
          'No observed PO-to-receipt measurement clears the {min}-receipt minimum. A Calculated PO-to-receipt source uses an assumed base of ~{days} days.',
          {
            context: 'lead time resolution summary (unmeasured)',
            min: detail.minSamples,
            days: detail.fallbackDays,
          },
        );
  }

  return gt(
    'Observed PO-to-receipt is ~{days} days ({n} receipts). Rungs need at least {min} receipts to be used.',
    {
      context: 'lead time resolution summary',
      days: Math.round(detail.resolved.leadTimeDays),
      n: detail.resolved.sampleN,
      min: detail.minSamples,
    },
  );
}

/**
 * The estimate frozen onto the suggestion being reviewed, shown only when
 * nothing else on screen accounts for it. An unmeasured ladder plus a declared
 * value that matches means the number is LIVE, not stale (a fresh run would
 * produce it again), and repeating it in a warning panel was exactly what made
 * a current estimate read as a leftover.
 */
function SavedEstimateNotice({
  detail,
  displayedEstimateDays,
}: {
  detail: GetLeadTimeDetailResponse;
  displayedEstimateDays: number | null;
}) {
  const gt = useGT();
  if (displayedEstimateDays === null) return null;
  if (detail.resolved.leadTimeDays !== null) return null;
  const explainedByDeclared = detail.declared.some(
    (entry) =>
      Math.round(entry.leadTimeDays) === Math.round(displayedEstimateDays),
  );
  if (explainedByDeclared) return null;

  return (
    <div className="flex items-start justify-between rounded-md border border-amber-200 bg-amber-50 p-3">
      <div>
        <p className="text-xs font-medium text-amber-900">
          {gt('Saved suggestion estimate', {
            context: 'persisted lead time estimate label',
          })}
        </p>
        <p className="text-lg font-semibold tabular-nums text-amber-950">
          {gt('~{days} days', {
            context: 'persisted lead time estimate value',
            days: Math.round(displayedEstimateDays),
          })}
        </p>
      </div>
      <TooltipInfoIcon
        ariaLabel={gt('How this estimate was determined', {
          context: 'persisted lead time estimate help label',
        })}
        description={gt(
          'Saved when this suggestion was generated, from observed receipt history or the supplier’s declared lead time. Neither matches it now, so it predates a change and a fresh run would size the line differently.',
          { context: 'persisted lead time estimate help text' },
        )}
        className="mt-1 text-amber-800 hover:text-amber-950"
      />
    </div>
  );
}

interface LeadTimeDetailDialogProps extends LeadTimeIdentity {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display handle for the dialog title (SKU or product name). */
  productLabel: string | null;
  /** When opened from a buyer-board row, enables bulk lead-time overrides. */
  suggestionId?: string | null;
  highlightCartKey?: string | null;
  /**
   * Estimate persisted on the suggestion currently being reviewed. It can
   * outlive both the observed-statistics refresh and the declared value it
   * came from, so it is surfaced when neither of those explains it: see
   * {@link SavedEstimateNotice}.
   */
  displayedEstimateDays?: number | null;
}

export function LeadTimeDetailDialog({
  open,
  onOpenChange,
  productLabel,
  displayedEstimateDays = null,
  suggestionId = null,
  highlightCartKey = null,
  ...identity
}: LeadTimeDetailDialogProps) {
  const gt = useGT();
  const detail = useLeadTimeDetail(identity, open);
  const contentRef = useRef<HTMLDivElement>(null);
  // Radix dismisses on outside pointer*down* and a local-state close flushes
  // synchronously, so the dialog unmounts mid-gesture: the page's
  // pointer-events lock lifts and the gesture's trailing click lands on
  // whatever was underneath — on the boards, the suggestion row, opening the
  // row-level modal right after. Close on the gesture's click instead; the
  // page stays pointer-locked for the whole gesture, so nothing falls through.
  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (e: MouseEvent) => {
      if (!contentRef.current || !(e.target instanceof Node)) return;
      if (contentRef.current.contains(e.target)) return;
      // A portaled descendant is visually INSIDE the dialog but not inside
      // `contentRef`, so a plain `contains` check reads a click on it as an
      // outside click and closes the dialog under the user. The dialog renders
      // `TooltipInfoIcon`, whose `TooltipContent` mounts through a portal, so
      // clicking the visible tooltip bubble did exactly that.
      //
      // Two markers because the two libraries mark their portals differently
      // and this dialog currently renders both: Radix wraps every popper in
      // `[data-radix-popper-content-wrapper]`, Base UI marks its portal root
      // `[data-base-ui-portal]`. Tooltip is migrated; Popover and Select are
      // not yet, and the Radix half stays until they are. Keying on the portal
      // rather than on the tooltip covers anything added here later, which is
      // why it was written this way originally. Safe against poppers owned by
      // other components because the dialog is modal — its pointer-events lock
      // means nothing outside it can be opened.
      if (
        e.target instanceof Element &&
        e.target.closest(
          '[data-radix-popper-content-wrapper], [data-base-ui-portal]',
        )
      ) {
        return;
      }
      onOpenChange(false);
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [open, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      // See the document-click effect above: dismissal is handled there, on the
      // gesture's click rather than its pointerdown, so the built-in
      // outside-press dismissal stays off.
      disableBackdropClickClose
    >
      <DialogContent
        ref={contentRef}
        className="max-w-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            {gt('Lead time calculation', { context: 'lead time dialog title' })}
            {productLabel ? ` · ${productLabel}` : ''}
          </DialogTitle>
          <DialogDescription>
            {gt(
              'Median PO-to-receipt calendar days from closed purchase orders, most specific measurement first.',
              { context: 'lead time dialog subtitle' },
            )}
          </DialogDescription>
        </DialogHeader>

        {!identity.warehouseId ? (
          <p className="text-sm text-gray-500">
            {gt('No warehouse context for this row.', {
              context: 'lead time dialog no warehouse',
            })}
          </p>
        ) : detail.isError ? (
          <p className="text-sm text-red-600">
            {gt('Could not load the lead-time detail.', {
              context: 'lead time dialog error',
            })}
          </p>
        ) : !detail.data ? (
          <p className="text-sm text-gray-500">
            {gt('Loading…', { context: 'lead time dialog loading' })}
          </p>
        ) : (
          <div className="space-y-3">
            <LeadTimeBreakdown detail={detail.data} />
            <h4 className="text-xs font-medium">
              {gt('Observed PO-to-receipt', {
                context: 'lead time receipt measurement section heading',
              })}
            </h4>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="py-1 pr-3 text-[10px] font-medium uppercase text-gray-400">
                    {gt('Measured from', { context: 'lead time column' })}
                  </th>
                  <th className="py-1 pr-3 text-right text-[10px] font-medium uppercase text-gray-400">
                    {gt('Median', { context: 'lead time column' })}
                  </th>
                  <th className="py-1 pr-3 text-right text-[10px] font-medium uppercase text-gray-400">
                    {gt('Spread', { context: 'lead time column' })}
                  </th>
                  <th className="py-1 text-right text-[10px] font-medium uppercase text-gray-400">
                    {gt('Sample', { context: 'lead time column' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.data.override ? (
                  <OverrideRow override={detail.data.override} />
                ) : null}
                {/* Every ladder level, in resolution order — levels with no
                    stats row render as empty rungs so the ladder always reads
                    complete. The list comes from the contract enum, so it
                    stays in sync with the grains the backend can emit. */}
                {LeadTimeGrainSchema.options.map((grain) => {
                  const rung = detail.data?.rungs.find(
                    (r) => r.grain === grain,
                  );
                  return rung ? (
                    <RungRow key={grain} rung={rung} />
                  ) : (
                    <EmptyRungRow key={grain} grain={grain} />
                  );
                })}
              </tbody>
            </table>

            <DeclaredLeadTimePanel
              declared={detail.data.declared}
              fallbackDays={detail.data.fallbackDays}
              hasResolvedValue={detail.data.observed.leadTimeDays !== null}
            />

            <SavedEstimateNotice
              detail={detail.data}
              displayedEstimateDays={displayedEstimateDays}
            />

            <p className="text-xs text-gray-500">
              <ResolutionSummary detail={detail.data} />
            </p>

            {detail.data.computedAt && (
              <p className="text-[10px] text-gray-400">
                {gt('Lead times computed {date}', {
                  context: 'lead time refresh footnote',
                  date: new Date(detail.data.computedAt).toLocaleDateString(),
                })}
              </p>
            )}

            {identity.warehouseId && identity.productV3Key ? (
              <LeadTimeDetailOverrideBlock
                warehouseId={identity.warehouseId}
                productV3Key={identity.productV3Key}
                suggestionId={suggestionId}
                highlightCartKey={highlightCartKey}
              />
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ExpectedArrivalProps extends LeadTimeIdentity {
  /** Days to display; null renders nothing (no estimate exists). */
  days: number | null;
  /**
   * Days pinned by a lead-time override that is active right now, resolved
   * live by the read that supplied this row. When it disagrees with `days` the
   * override postdates the run that sized the row, so the pin shows as the
   * value with the saved estimate struck through (the same "effective over
   * calculated" shape the policy-override table uses). Null = no override.
   */
  overrideDays?: number | null;
  productLabel: string | null;
  className?: string;
  suggestionId?: string;
  highlightCartKey?: string;
}

/**
 * "Expected to arrive in ~N days" as a clickable annotation; the click opens
 * the calculation-detail dialog. Renders nothing when no estimate exists.
 */
export function ExpectedArrival({
  days,
  overrideDays = null,
  productLabel,
  className,
  suggestionId,
  highlightCartKey,
  ...identity
}: ExpectedArrivalProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  if (days === null && overrideDays === null) return null;

  // Only a pin that actually moves the number is worth two values: an override
  // the run already applied would render "7d 7d-struck".
  const pinned =
    overrideDays !== null &&
    (days === null || Math.round(overrideDays) !== Math.round(days));

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // preventDefault on top of stopPropagation: inside a stretched row
          // link (GentlyTable renderRowLink) navigation is the click's
          // *default* action, which stopPropagation cannot cancel.
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={`inline-flex items-center gap-1 text-xs text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-800 ${className ?? ''}`}
        title={
          pinned
            ? gt(
                'A total lead time override applies to current calculations. This line uses its saved estimate; regenerate to apply the override. Click to see the calculation.',
                { context: 'expected arrival tooltip with active override' },
              )
            : gt('Show how this lead time was calculated', {
                context: 'expected arrival tooltip',
              })
        }
      >
        <ClockIcon className="h-3 w-3" aria-hidden />
        {pinned && (
          <span className="text-amber-700">
            {gt('Changed since run', {
              context:
                'suggestion lead time manual pin changed since saved estimate',
            })}
          </span>
        )}
        {days !== null && (
          <span>
            {gt('~{days}d', {
              context: 'expected arrival text',
              days: Math.round(days),
            })}
          </span>
        )}
      </button>
      {open && (
        <LeadTimeDetailDialog
          open={open}
          onOpenChange={setOpen}
          productLabel={productLabel}
          displayedEstimateDays={days}
          suggestionId={suggestionId}
          highlightCartKey={highlightCartKey}
          {...identity}
        />
      )}
    </>
  );
}
